import React, { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Switch } from "@/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/shared/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Building2,
  Key,
  Settings,
  Copy,
  Eye,
  EyeOff,
  CheckCircle2,
  Save,
  Info,
  Percent,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import * as resellerService from "@/shared/api/services/resellerService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

function generateApiKey(prefix = "mrc_pk_") {
  return prefix + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

export default function ResellerMerchantForm({ open, onClose, merchant, onSave, saving }) {
  const { t } = useLanguage();
  const isEdit = !!merchant;

  // Fetch reseller profile to show parent commission
  const { data: resellerProfile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: () => resellerService.getProfile(),
    enabled: open, // Fetch when form is open (both create and edit)
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    status: "ACTIVE",
    code: "",
    api_key: generateApiKey("mrc_pk_"),
    secret_key: generateApiKey("mrc_sk_"),
    callback_url: "",
    webhook_url: "",
    deposit_enabled: true,
    withdrawal_enabled: true,
    min_deposit: 100,
    max_deposit: 1000000,
    min_withdrawal: 100,
    max_withdrawal: 500000,
    fee_type: "percentage",
    fee_value: '',
    settlement_period: "T1",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (merchant) {
      // When editing, populate fee_value from merchant's depositCommission
      const feeValue = merchant.depositCommission ?? 2.5;
      setFormData(prev => ({
        ...prev,
        ...merchant,
        password: "",
        fee_value: feeValue,
        fee_type: "percentage", // Default to percentage
      }));
    } else {
      // When creating, auto-populate fee_value from reseller profile
      const feeValue = resellerProfile?.depositCommission ?? 2.5;
      setFormData({
        name: "",
        email: "",
        password: "",
        status: "ACTIVE",
        code: "",
        api_key: generateApiKey("mrc_pk_"),
        secret_key: generateApiKey("mrc_sk_"),
        callback_url: "",
        webhook_url: "",
        deposit_enabled: true,
        withdrawal_enabled: true,
        min_deposit: 100,
        max_deposit: 1000000,
        min_withdrawal: 100,
        max_withdrawal: 500000,
        fee_type: "percentage",
        fee_value: feeValue,
        settlement_period: "T1",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        notes: "",
      });
    }
  }, [merchant, open, resellerProfile]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = () => {
    if (merchant) {
      // Update - send only updateable fields
      onSave({
        name: formData.name,
        status: formData.status,
      });
    } else {
      // Create - send required fields for backend
      onSave({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        depositCommission: formData.fee_value && formData.fee_type === "percentage" ? formData.fee_value : undefined,
        withdrawalCommission: undefined, // Can be added if needed
        percentageFee: formData.fee_value && formData.fee_type === "percentage" ? formData.fee_value : undefined,
        webhook: formData.webhook_url || undefined,
        callback: formData.callback_url || undefined,
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEdit ? t('editMerchant') : t('addNewMerchant')}</SheetTitle>
          <SheetDescription>
            {isEdit ? t('updateMerchantDetails') : t('createMerchantUnderReseller')}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="general" className="mt-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="general" className="text-xs">
              <Building2 className="h-3 w-3 mr-1" />
              {t('general')}
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-xs">
              <Settings className="h-3 w-3 mr-1" />
              {t('settings')}
            </TabsTrigger>
            <TabsTrigger value="api" className="text-xs">
              <Key className="h-3 w-3 mr-1" />
              {t('apiKeys')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('businessNameRequired')}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder={t('merchantName')}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>{t('merchantCode')}</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                  placeholder={t('merchantCodePlaceholder')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('loginEmailRequired')}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  disabled={isEdit}
                  required
                />
                {isEdit && <p className="text-xs text-slate-500">{t('emailCannotBeChanged')}</p>}
              </div>
              {!isEdit && (
                <div className="space-y-2">
                  <Label>{t('passwordRequired')}</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    placeholder={t('enterPassword')}
                    required
                  />
                </div>
              )}
              {isEdit && (
                <div className="space-y-2">
                  <Label>{t('status')}</Label>
                  <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACTIVE">{t('active')}</SelectItem>
                      <SelectItem value="INACTIVE">{t('inactive')}</SelectItem>
                      <SelectItem value="SUSPENDED">{t('suspended')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('status')}</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange("status", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t('active')}</SelectItem>
                    <SelectItem value="PENDING">{t('pending')}</SelectItem>
                    <SelectItem value="SUSPENDED">{t('suspended')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('settlementPeriod')}</Label>
                <Select value={formData.settlement_period} onValueChange={(v) => handleChange("settlement_period", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="T0">{t('t0SameDay')}</SelectItem>
                    <SelectItem value="T1">{t('t1NextDay')}</SelectItem>
                    <SelectItem value="T3">{t('t3')}</SelectItem>
                    <SelectItem value="T7">{t('t7Weekly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('contactName')}</Label>
                <Input
                  value={formData.contact_name}
                  onChange={(e) => handleChange("contact_name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('contactEmail')}</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => handleChange("contact_email", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('contactPhone')}</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => handleChange("contact_phone", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('callbackUrl')}</Label>
              <Input
                value={formData.callback_url}
                onChange={(e) => handleChange("callback_url", e.target.value)}
                placeholder={t('callbackUrlPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('webhookUrl')}</Label>
              <Input
                value={formData.webhook_url}
                onChange={(e) => handleChange("webhook_url", e.target.value)}
                placeholder={t('webhookUrlPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                rows={2}
              />
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4 mt-4">
            {/* Parent Commission Info (when creating new merchant) */}
            {!isEdit && resellerProfile && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900 mb-2">
                      {resellerProfile.parentName
                        ? t('givenBy').replace('{type}', resellerProfile.parentType).replace('{name}', resellerProfile.parentName)
                        : t('yourCommissionRates')}
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-blue-700 font-medium">{t('depositCommission')}</p>
                        <p className="text-blue-900 text-lg font-semibold">
                          {resellerProfile.depositCommission !== null && resellerProfile.depositCommission !== undefined
                            ? `${resellerProfile.depositCommission}%`
                            : t('nA')}
                        </p>
                      </div>
                      <div>
                        <p className="text-blue-700 font-medium">{t('withdrawalCommission')}</p>
                        <p className="text-blue-900 text-lg font-semibold">
                          {resellerProfile.withdrawalCommission !== null && resellerProfile.withdrawalCommission !== undefined
                            ? `${resellerProfile.withdrawalCommission}%`
                            : t('nA')}
                        </p>
                      </div>
                      <div>
                        <p className="text-blue-700 font-medium">{t('percentageFee')}</p>
                        <p className="text-blue-900 text-lg font-semibold">
                          {resellerProfile.percentageFee !== null && resellerProfile.percentageFee !== undefined
                            ? `${resellerProfile.percentageFee}%`
                            : t('nA')}
                        </p>
                      </div>
                    </div>
                    {resellerProfile.parentName && (
                      <p className="text-xs text-blue-600 mt-2">
                        {t('commissionGivenToYouBy').replace('{name}', resellerProfile.parentName)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{t('enableDeposits')}</p>
                  <p className="text-xs text-slate-500">{t('allowDepositTransactions')}</p>
                </div>
                <Switch
                  checked={formData.deposit_enabled}
                  onCheckedChange={(v) => handleChange("deposit_enabled", v)}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{t('enableWithdrawals')}</p>
                  <p className="text-xs text-slate-500">{t('allowWithdrawalTransactions')}</p>
                </div>
                <Switch
                  checked={formData.withdrawal_enabled}
                  onCheckedChange={(v) => handleChange("withdrawal_enabled", v)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>{t('minDepositThb')}</Label>
                <Input
                  type="number"
                  value={formData.min_deposit}
                  onChange={(e) => handleChange("min_deposit", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('maxDepositThb')}</Label>
                <Input
                  type="number"
                  value={formData.max_deposit}
                  onChange={(e) => handleChange("max_deposit", parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('minWithdrawalThb')}</Label>
                <Input
                  type="number"
                  value={formData.min_withdrawal}
                  onChange={(e) => handleChange("min_withdrawal", parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('maxWithdrawalThb')}</Label>
                <Input
                  type="number"
                  value={formData.max_withdrawal}
                  onChange={(e) => handleChange("max_withdrawal", parseFloat(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div className="space-y-2">
                <Label>{t('feeType')}</Label>
                <Select value={formData.fee_type} onValueChange={(v) => handleChange("fee_type", v)} disabled={true}>
                  <SelectTrigger className="bg-slate-50 cursor-not-allowed">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t('percentage')}</SelectItem>
                    <SelectItem value="fixed">{t('fixed')}</SelectItem>
                    <SelectItem value="tiered">{t('tiered')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('feeValue')} ({formData.fee_type === "percentage" ? "%" : "฿"})</Label>
                <Input
                  type="number"
                  step={formData.fee_type === "percentage" ? "0.1" : "0.01"}
                  value={formData.fee_value}
                  onChange={(e) => handleChange("fee_value", parseFloat(e.target.value) || 0)}
                  disabled={true}
                  className="bg-slate-50 cursor-not-allowed"
                />
                <p className="text-xs text-slate-500">
                  {t('feeValueSetToBy')
                    .replace('{value}', formData.fee_value)
                    .replace('{name}', resellerProfile?.parentName || t('parent'))}
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="api" className="space-y-4 mt-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                {t('apiKeysAutoGenerated')}
              </p>
            </div>

            <div className="space-y-2">
              <Label>{t('apiKeyPublic')}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-2.5 bg-slate-900 rounded-lg font-mono text-xs text-white overflow-x-auto">
                  {showApiKey ? formData.api_key : "••••••••••••••••••••••••••••••••"}
                </div>
                <Button variant="outline" size="icon" onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.api_key, "api")}>
                  {copied === "api" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('secretKey')}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-2.5 bg-slate-900 rounded-lg font-mono text-xs text-white overflow-x-auto">
                  {showSecretKey ? formData.secret_key : "••••••••••••••••••••••••••••••••"}
                </div>
                <Button variant="outline" size="icon" onClick={() => setShowSecretKey(!showSecretKey)}>
                  {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(formData.secret_key, "secret")}>
                  {copied === "secret" ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => onClose(false)}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.name || (!isEdit && (!formData.email || !formData.password))}
            className="gap-2 bg-violet-600 hover:bg-violet-700"
          >
            <Save className="h-4 w-4" />
            {saving ? t('saving') : isEdit ? t('update') : t('create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
